// @ts-check
import {apiClient} from '../../../util/api-client';
import {apiHost} from '../../util';
import {BaseClient} from '../utils/base-client';

/**
 * @typedef {object} AnnotationUser
 * @property {number} id
 * @property {string} first_name
 * @property {string} last_name
 *
 * @typedef {object} Annotation
 * @property {number} id
 * @property {number} project_id
 * @property {string} date
 * @property {string} description
 * @property {object} highchartsDate
 * @property {AnnotationUser} [user]
 */

class AnnotationsClient extends BaseClient {
  /**
   * @param {Object} params
   * @param {number} params.projectId - the projectId you want to query
   * @param {number=} params.id - the id of the annotation you want to query (for annotation-specific queries)
   */
  buildEndpoint({projectId, id}) {
    const path = [`api`, `app`, `projects`, projectId, `annotations`];
    if (id) {
      path.push(id);
    }
    return path.join(`/`) + `/`; // add trailing slash to avoid an extra redirect
  }

  /**
   * @param {object} params
   * @param {string} params.fromDate - start date in '%Y-%m-%d' format
   * @param {string} params.toDate - end date in '%Y-%m-%d' format
   */
  buildQueryParams({fromDate, toDate}) {
    return {
      fromDate,
      toDate,
    };
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id you want to query annotations for
   * @param {string} params.fromDate - start date in '%Y-%m-%d' format
   * @param {string} params.toDate - end date in '%Y-%m-%d' format
   * @returns {Promise<Annotation[]>} results
   */
  query({projectId, fromDate, toDate}) {
    // Intentionally turning off caching for annotations so we don't have to keep it in sync
    // when we create, update, delete
    return super.query({projectId, fromDate, toDate}, {clearCache: true});
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id this annotation belongs to
   * @param {object} params.annotation
   * @param {string} params.annotation.date - the date and time of the annotation in '%Y-%m-%d %H:%M:%S' (example: 2018-12-05 13:00:00)
   * @param {string} params.annotation.description - the content of the annotation
   * @returns {Promise<Annotation>} response
   */
  async createAnnotation({projectId, annotation}) {
    return await apiClient.post(`${apiHost()}/${this.buildEndpoint({projectId})}`, annotation);
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id this annotation belongs to
   * @param {number} params.id - the id of the annotation to update
   * @param {string} params.description - the updated description
   * @returns {Promise<Annotation>} response
   */
  async updateAnnotation({projectId, id, description}) {
    return await apiClient.patch(`${apiHost()}/${this.buildEndpoint({projectId, id})}`, {description});
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id this annotation belongs to
   * @param {number} params.id - the id of the annotation to delete
   * @returns {Promise<{id: number}>} response
   */
  async deleteAnnotation({projectId, id}) {
    return await apiClient.delete(`${apiHost()}/${this.buildEndpoint({projectId, id})}`);
  }

  processResponse(response) {
    return response.results;
  }
}

export default new AnnotationsClient();
